/**
 * Step 8 · AI Review & Create
 *
 * Final review before the draft is promoted. Shows the AI summary +
 * the full field configuration (sectioned) with per-field status
 * badges. From here the user hits "Create Project" (footer action)
 * and the wizard promotes the draft in place.
 */
import { useEffect, useRef } from "react";
import {
  Sparkle, ArrowClockwise, CheckCircle, Warning, Lightning,
} from "@phosphor-icons/react";
import { StepHeader } from "./Step1Identity";

const SECTIONS = [
  { key: "identity",       label: "Identity",       icon: Sparkle },
  { key: "location",       label: "Location",       icon: Sparkle },
  { key: "commodity",      label: "Commodity",      icon: Sparkle },
  { key: "crs",             label: "CRS",             icon: Sparkle },
  { key: "modelling",       label: "Modelling",       icon: Sparkle },
  { key: "classification", label: "Classification", icon: Sparkle },
  { key: "team",            label: "Team & Reporting", icon: Sparkle },
];

const TONE = {
  "AI Suggested":              "border-brand-blue/30 bg-brand-blue/5 text-brand-blue",
  "Draft":                      "border-slate-200 bg-slate-50 text-slate-600",
  "Manually Edited":            "border-amber-200 bg-amber-50 text-amber-800",
  "Validated":                  "border-sky-200 bg-sky-50 text-sky-700",
  "Technically Approved":       "border-emerald-200 bg-emerald-50 text-emerald-700",
  "CP/QP Approved":             "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Locked for Publication":     "border-violet-200 bg-violet-50 text-violet-800",
};

export default function Step8AIReview({
  cfg, aiPreview, aiPreviewing, aiPreviewResult, aiApply, aiApplying,
  form, canCreate, draftId,
}) {
  const requestedRef = useRef(false);
  useEffect(() => {
    if (!draftId) return;
    if (requestedRef.current) return;
    if (aiPreviewResult) return;
    requestedRef.current = true;
    aiPreview({}).catch(() => { requestedRef.current = false; });
    // Intentionally omit `aiPreview` from deps — it's an inline arrow whose
    // reference is not stable across parent renders. Guard via ref instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, aiPreviewResult]);

  return (
    <section className="space-y-4" data-testid="step-8-review">
      <StepHeader step="Step 8" title="AI Review & Create"
        subtitle="Review the final configuration. Manual values are highlighted amber; AI suggestions are blue. Locked fields cannot be silently changed later." />

      {/* AI summary strip */}
      <div className="rounded-lg border border-brand-blue/20 bg-brand-blue/5 p-4" data-testid="ai-summary-strip">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 shrink-0 rounded-md bg-brand-blue/10 flex items-center justify-center">
            <Sparkle size={14} className="text-brand-blue" weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[13px] text-slate-900">SRES AI Configuration Review</div>
            <div className="text-[12px] text-slate-700 mt-1" data-testid="ai-summary-text">
              {aiPreviewResult?.summary || "Loading AI configuration review…"}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button type="button" onClick={() => aiPreview({})} disabled={aiPreviewing}
                        data-testid="btn-refresh-ai"
                        className="inline-flex items-center gap-1 rounded-md border border-brand-blue/30 hover:bg-brand-blue/10 px-2.5 h-8 text-[11px] text-brand-blue font-medium disabled:opacity-50">
              <ArrowClockwise size={11} className={aiPreviewing ? "animate-spin" : ""} /> Refresh
            </button>
            <button type="button" onClick={() => aiApply({})} disabled={aiApplying || !draftId}
                        data-testid="btn-apply-all-ai"
                        className="inline-flex items-center gap-1 rounded-md bg-brand-blue hover:bg-brand-blue/90 text-white px-2.5 h-8 text-[11px] font-medium disabled:opacity-50">
              <Lightning size={11} weight="fill" /> Apply all suggestions
            </button>
          </div>
        </div>
      </div>

      {/* Section summary grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {SECTIONS.map((s) => (
          <SectionSummary key={s.key} label={s.label} slots={cfg?.[s.key] || {}} />
        ))}
      </div>

      {/* Create readiness */}
      <div className={`rounded-lg border p-4 flex items-center gap-3 ${canCreate ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
              data-testid="create-readiness">
        {canCreate ? (
          <><CheckCircle size={18} className="text-emerald-600" weight="fill" />
            <div className="flex-1">
              <div className="font-semibold text-[13px] text-emerald-800">Ready to Create</div>
              <div className="text-[11.5px] text-emerald-700">Click <b>Create Project</b> in the footer to promote the draft. Existing uploads will be preserved.</div>
            </div></>
        ) : (
          <><Warning size={18} className="text-amber-600" weight="fill" />
            <div className="flex-1">
              <div className="font-semibold text-[13px] text-amber-800">Missing Required Fields</div>
              <div className="text-[11.5px] text-amber-700">Ensure Name, Country, Commodity and Reporting Standard are set (Steps 1, 2, 7).</div>
            </div></>
        )}
      </div>
    </section>
  );
}

function SectionSummary({ label, slots }) {
  const entries = Object.entries(slots).filter(([, s]) => s?.value != null && s?.value !== "");
  if (entries.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
        <div className="text-[11px] text-slate-400">No values set.</div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`summary-${label.toLowerCase().replaceAll(/\s+/g,"-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center justify-between">
        <span>{label}</span>
        <span className="font-mono text-slate-400">{entries.length}</span>
      </div>
      <dl className="space-y-1.5">
        {entries.slice(0, 8).map(([k, s]) => (
          <div key={k} className="grid grid-cols-[110px_1fr] gap-2 items-start">
            <dt className="text-[10.5px] text-slate-500 truncate">{k}</dt>
            <dd className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="font-mono text-[11.5px] text-slate-800 truncate">{String(s.value)}</span>
              <span className={`inline-flex items-center px-1 py-0.5 rounded-sm border text-[8.5px] uppercase tracking-widest ${TONE[s.review_status] || TONE.Draft}`}>
                {s.review_status || "Draft"}
              </span>
            </dd>
          </div>
        ))}
        {entries.length > 8 && (
          <div className="text-[10.5px] text-slate-400 pt-1 italic">+ {entries.length - 8} more (see Command Center after create)</div>
        )}
      </dl>
    </div>
  );
}
